import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DAYS } from "@/data/itinerary";
import { STORAGE_KEYS, storage } from "@/lib/storage";
import { Download, Save } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
  const d = new Date(iso);
  return d
    .toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })
    .replace(/^./, (c) => c.toUpperCase());
}

export default function Diary() {
  const [entries, setEntries] = useState({});

  useEffect(() => {
    setEntries(storage.get(STORAGE_KEYS.diary, {}));
  }, []);

  const update = (id, value) => {
    const next = { ...entries, [id]: value };
    setEntries(next);
  };

  const saveAll = () => {
    storage.set(STORAGE_KEYS.diary, entries);
    toast.success("Diario salvato");
  };

  const exportTxt = () => {
    const lines = [`Diario di viaggio — Isola d'Elba 22-29 Agosto 2026`, ""];
    DAYS.forEach((d) => {
      const text = entries[d.id];
      if (text && text.trim()) {
        lines.push(`— Giorno ${d.id} · ${formatDate(d.date)} · ${d.title}`);
        lines.push(text.trim());
        lines.push("");
      }
    });
    const blob = new Blob([lines.join("\n")], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "diario-elba-2026.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Diario esportato");
  };

  const wrote = Object.values(entries).filter((v) => v && v.trim()).length;

  return (
    <div data-testid="diary-page" className="pt-28 pb-24">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="font-accent text-xs text-[#D96C4A] mb-3">
              I nostri ricordi
            </div>
            <h1 className="font-display text-5xl md:text-6xl text-[#1D3A4C]">
              Diario di viaggio
            </h1>
            <p className="mt-3 text-[#5C7180] max-w-xl">
              Scrivi impressioni, piatti, tramonti. {wrote}/{DAYS.length} giorni
              raccontati.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="diary-save-all"
              onClick={saveAll}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D96C4A] hover:bg-[#b8542f] text-white text-sm font-medium transition-colors"
            >
              <Save className="w-4 h-4" />
              Salva tutto
            </button>
            <button
              data-testid="diary-export"
              onClick={exportTxt}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#1D3A4C]/20 text-sm text-[#1D3A4C] hover:bg-[#1D3A4C]/5 transition-colors"
            >
              <Download className="w-4 h-4" />
              Esporta
            </button>
          </div>
        </div>

        <div className="space-y-8">
          {DAYS.map((d) => (
            <div
              key={d.id}
              data-testid={`diary-entry-${d.id}`}
              className="grid md:grid-cols-12 gap-6 p-6 md:p-8 rounded-2xl bg-white border border-[#1D3A4C]/10"
            >
              <div className="md:col-span-4">
                <div className="font-accent text-[10px] text-[#8A9BA8]">
                  {formatDate(d.date)}
                </div>
                <Link
                  to={`/giorno/${d.id}`}
                  className="mt-2 inline-block font-display text-2xl text-[#1D3A4C] hover:text-[#D96C4A] transition-colors"
                >
                  {d.title}
                </Link>
                <div className="mt-1 text-xs text-[#8A9BA8]">{d.zone}</div>
              </div>
              <div className="md:col-span-8">
                <textarea
                  data-testid={`diary-textarea-${d.id}`}
                  value={entries[d.id] || ""}
                  onChange={(e) => update(d.id, e.target.value)}
                  onBlur={() =>
                    storage.set(STORAGE_KEYS.diary, entries)
                  }
                  rows={5}
                  placeholder={`Racconta il giorno ${d.id}: cosa hai visto, chi hai incontrato, cosa hai mangiato…`}
                  className="w-full bg-[#FAF8F4] rounded-xl border border-[#1D3A4C]/10 focus:border-[#D96C4A] outline-none p-4 text-[#1D3A4C] placeholder-[#8A9BA8] resize-vertical transition-colors leading-relaxed"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
